import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { wholesale_forecast_by_account_products_path } from "@/lib/routes";
import { useMarket } from "@/lib/useMarket";

export interface ProductResult {
  external_id: string;
  name: string;
  short_name?: string | null;
}

interface Props {
  existingExternalIds: Set<string>;
  onPick: (product: ProductResult) => void;
}

const DEBOUNCE_MS = 300;

// Flat org-wide product search: name / short name / product line short name
// (substring) or product ID (prefix). Fallback for orgs without a current
// TopDown plan; the drill-down picker is the primary flow.
export default function FlatProductSearch({ existingExternalIds, onPick }: Props) {
  const market = useMarket();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<ProductResult[]>([]);
  const [loading, setLoading] = useState(false);
  const requestId = useRef(0);

  useEffect(() => {
    const id = ++requestId.current;
    setLoading(true);
    const timer = setTimeout(() => {
      const url = `${wholesale_forecast_by_account_products_path(market.slug)}?q=${encodeURIComponent(query)}`;
      fetch(url, { headers: { Accept: "application/json" } })
        .then((res) => res.json())
        .then((json: ProductResult[]) => {
          if (id === requestId.current) setResults(json);
        })
        .finally(() => {
          if (id === requestId.current) setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, market.slug]);

  return (
    <>
      <Input
        autoFocus
        placeholder="Search products…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      <div className="max-h-72 overflow-y-auto rounded-md border">
        {loading && (
          <div className="px-3 py-4 text-center text-xs text-muted-foreground">Searching…</div>
        )}
        {!loading && results.length === 0 && (
          <div className="px-3 py-4 text-center text-xs text-muted-foreground">
            No matching products.
          </div>
        )}
        {!loading &&
          results.map((p) => {
            const added = existingExternalIds.has(p.external_id);
            return (
              <Button
                key={p.external_id}
                type="button"
                variant="ghost"
                disabled={added}
                onClick={() => onPick(p)}
                className="h-auto w-full justify-between gap-2 rounded-none border-b px-3 py-2 text-left font-normal last:border-b-0"
              >
                <span className="truncate text-foreground">{p.name}</span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {added ? "already added" : `#${p.external_id}`}
                </span>
              </Button>
            );
          })}
      </div>
    </>
  );
}
